import React, { useState, useEffect } from 'react';
import { colors, radius, fonts } from '../theme';
import { reservations } from '../api';

// 12 tables fictives du restaurant
const RESTAURANT_TABLES = [
  { id: 1, name: 'Table 1', capacity: 2, x: 0, y: 0, shape: 'round' as const },
  { id: 2, name: 'Table 2', capacity: 2, x: 1, y: 0, shape: 'round' as const },
  { id: 3, name: 'Table 3', capacity: 4, x: 2, y: 0, shape: 'square' as const },
  { id: 4, name: 'Table 4', capacity: 4, x: 3, y: 0, shape: 'square' as const },
  { id: 5, name: 'Table 5', capacity: 6, x: 0, y: 1, shape: 'rect' as const },
  { id: 6, name: 'Table 6', capacity: 6, x: 1, y: 1, shape: 'rect' as const },
  { id: 7, name: 'Table 7', capacity: 4, x: 2, y: 1, shape: 'square' as const },
  { id: 8, name: 'Table 8', capacity: 2, x: 3, y: 1, shape: 'round' as const },
  { id: 9, name: 'Table 9', capacity: 8, x: 0, y: 2, shape: 'rect' as const },
  { id: 10, name: 'Table 10', capacity: 6, x: 1, y: 2, shape: 'rect' as const },
  { id: 11, name: 'Table 11', capacity: 4, x: 2, y: 2, shape: 'square' as const },
  { id: 12, name: 'Table 12', capacity: 2, x: 3, y: 2, shape: 'round' as const },
];

export function ReservationsPage() {
  const [tab, setTab] = useState<'book' | 'my' | 'checkin'>('book');
  const [packages, setPackages] = useState<any[]>([]);
  const [tables, setTables] = useState<any[]>([]);
  const [myRes, setMyRes] = useState<any[]>([]);
  const [form, setForm] = useState({ package_id: '', table_id: '', date: '', time_slot: '', guests_count: 2, special_requests: '' });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);

  // Tables réservées (simulées côté frontend selon les réservations existantes)
  const [reservedTableIds, setReservedTableIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    reservations.packages().then(r => setPackages(r.data)).catch(() => {});
    reservations.tables().then(r => setTables(r.data)).catch(() => {});
    refreshMy();
  }, []);

  const refreshMy = () => {
    reservations.my().then(r => {
      setMyRes(r.data);
      // Marquer les tables réservées pour la date/creneau sélectionnés
      const reserved = new Set<number>();
      if (form.date && form.time_slot) {
        r.data.forEach((res: any) => {
          if (res.status === 'confirmed' && res.date === form.date && res.time_slot === form.time_slot && res.table_id) {
            reserved.add(res.table_id);
          }
        });
      }
      setReservedTableIds(reserved);
    }).catch(() => {});
  };

  // Update reserved tables when form date/time changes
  useEffect(() => {
    if (form.date && form.time_slot) {
      const reserved = new Set<number>();
      myRes.forEach((res: any) => {
        if (res.status === 'confirmed' && res.date === form.date && res.time_slot === form.time_slot && res.table_id) {
          reserved.add(res.table_id);
        }
      });
      setReservedTableIds(reserved);
    } else {
      setReservedTableIds(new Set());
    }
  }, [form.date, form.time_slot]);

  // Quand on clique sur une table du plan
  const handleTableClick = (tableId: number) => {
    if (reservedTableIds.has(tableId)) return; // Table réservée, non cliquable
    setSelectedTableId(tableId);
    setForm({ ...form, table_id: String(tableId) });
  };

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await reservations.create({
        ...form,
        package_id: form.package_id ? Number(form.package_id) : null,
        table_id: form.table_id ? Number(form.table_id) : null,
        guests_count: Number(form.guests_count),
      });
      setMsg('Réservation confirmée !');
      setForm({ package_id: '', table_id: '', date: '', time_slot: '', guests_count: 2, special_requests: '' });
      setSelectedTableId(null);
      refreshMy();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  const handleCheckIn = async (reservationId?: number) => {
    setBusy(true);
    setMsg('');
    try {
      await reservations.checkIn(reservationId);
      setMsg('Check-in réussi ! Bienvenue au Siam.');
      refreshMy();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Erreur check-in');
    }
    setBusy(false);
  };

  const handleCheckOut = async () => {
    setBusy(true);
    setMsg('');
    try {
      await reservations.checkOut();
      setMsg('Merci pour votre visite ! À bientôt.');
      refreshMy();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  const statusColor: Record<string, string> = {
    confirmed: colors.success, pending: colors.orange, cancelled: colors.error, completed: colors.info,
  };

  const statusLabel: Record<string, string> = {
    confirmed: 'Confirmée', pending: 'En attente', cancelled: 'Annulée', completed: 'Terminée',
  };

  // Trouver la capacité d'une table du plan
  const getTableInfo = (id: number) => RESTAURANT_TABLES.find(t => t.id === id);

  return (
    <div>
      <h2 style={{ color: colors.gold, fontSize: 24, marginBottom: 8 }}>Réservations</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {(['book', 'my', 'checkin'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={tab === t ? tabActive : tabStyle}>
            {t === 'book' ? '📅 Réserver' : t === 'my' ? '📋 Mes réservations' : '✅ Check-in/out'}
          </button>
        ))}
      </div>

      {msg && <div style={{ color: msg.includes('!') ? colors.success : colors.error, background: `${msg.includes('!') ? colors.success : colors.error}15`, padding: 12, borderRadius: radius.sm, marginBottom: 16, fontSize: 14 }}>{msg}</div>}

      {/* BOOK TAB */}
      {tab === 'book' && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' as const }}>
          {/* Plan de table - à gauche */}
          <div style={{ flex: '1 1 400px', minWidth: 340 }}>
            <div style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}` }}>
              <h3 style={{ color: colors.text, fontSize: 18, marginBottom: 8 }}>🪑 Plan de table</h3>
              <p style={{ color: colors.textMuted, fontSize: 12, marginBottom: 16 }}>
                {!form.date || !form.time_slot
                  ? '⏰ Sélectionnez une date et un créneau pour voir les disponibilités'
                  : '👆 Cliquez sur une table disponible pour la sélectionner'}
              </p>

              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: colors.success + '30', border: `2px solid ${colors.success}` }} />
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>Disponible</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: colors.error + '30', border: `2px solid ${colors.error}` }} />
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>Réservée</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: colors.gold + '30', border: `2px solid ${colors.gold}` }} />
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>Sélectionnée</span>
                </div>
              </div>

              {/* Rectangle du restaurant */}
              <div style={{
                background: '#0F0F23',
                borderRadius: radius.md,
                border: `2px dashed ${colors.border}`,
                padding: 24,
                position: 'relative',
                minHeight: 320,
              }}>
                {/* Label entrée */}
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <span style={{ color: colors.gold, fontSize: 11, textTransform: 'uppercase', letterSpacing: 2 }}>🚪 Entrée</span>
                </div>

                {/* Grille de tables */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 16,
                  maxWidth: 400,
                  margin: '0 auto',
                }}>
                  {RESTAURANT_TABLES.map(table => {
                    const isReserved = reservedTableIds.has(table.id);
                    const isSelected = selectedTableId === table.id;
                    const isClickable = form.date && form.time_slot && !isReserved;

                    const tableColor = isReserved ? colors.error : isSelected ? colors.gold : colors.success;
                    const bgOpacity = isReserved ? '0.25' : isSelected ? '0.2' : '0.12';
                    const borderW = isSelected ? '3px' : '2px';

                    return (
                      <button
                        key={table.id}
                        onClick={() => handleTableClick(table.id)}
                        disabled={!isClickable}
                        style={{
                          background: `${tableColor}${bgOpacity === '0.25' ? '40' : bgOpacity === '0.2' ? '33' : '1F'}`,
                          border: `${borderW} solid ${tableColor}`,
                          borderRadius: table.shape === 'round' ? '50%' : table.shape === 'square' ? radius.sm : radius.md,
                          padding: 0,
                          cursor: isClickable ? 'pointer' : isReserved ? 'not-allowed' : 'default',
                          display: 'flex',
                          flexDirection: 'column' as const,
                          alignItems: 'center',
                          justifyContent: 'center',
                          aspectRatio: table.shape === 'round' ? '1' : table.shape === 'rect' ? '1.4' : '1',
                          minWidth: 72,
                          opacity: isReserved ? 0.7 : 1,
                          transition: 'all 0.2s ease',
                          position: 'relative' as const,
                        }}
                      >
                        {isReserved ? (
                          <>
                            <span style={{ fontSize: 18 }}>🚫</span>
                            <span style={{ color: colors.error, fontSize: 9, fontWeight: 'bold' as const, marginTop: 2 }}>RÉSERVÉE</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 16, opacity: 0.8 }}>{table.shape === 'round' ? '⭕' : table.shape === 'rect' ? '🟩' : '⬜'}</span>
                            <span style={{ color: colors.text, fontSize: 11, fontWeight: 600, marginTop: 2 }}>{table.name}</span>
                            <span style={{ color: colors.textMuted, fontSize: 9 }}>{table.capacity} pers.</span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Label bar / cuisine */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>🍷 Bar</span>
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>🍽️ Cuisine</span>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire - à droite */}
          <div style={{ flex: '1 1 300px', minWidth: 280, maxWidth: 500 }}>
            <form onSubmit={handleBook} style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}` }}>
              <h3 style={{ color: colors.text, fontSize: 18, marginBottom: 16 }}>Nouvelle réservation</h3>

              <label style={labelStyle}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required style={inputStyle} />

              <label style={labelStyle}>Créneau horaire</label>
              <select value={form.time_slot} onChange={e => setForm({ ...form, time_slot: e.target.value })} required style={inputStyle}>
                <option value="">Choisir...</option>
                {['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <label style={labelStyle}>Nombre de convives</label>
              <input type="number" min={1} max={20} value={form.guests_count} onChange={e => setForm({ ...form, guests_count: Number(e.target.value) })} style={inputStyle} />

              <label style={labelStyle}>Package (facultatif)</label>
              <select value={form.package_id} onChange={e => setForm({ ...form, package_id: e.target.value })} style={inputStyle}>
                <option value="">Aucun</option>
                {packages.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name} — {p.min_spend}€ min</option>
                ))}
              </select>

              {/* Table sélectionnée via le plan */}
              {selectedTableId && (
                <div style={{ background: colors.gold + '15', border: `1px solid ${colors.gold}40`, borderRadius: radius.sm, padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ color: colors.gold, fontSize: 13, fontWeight: 'bold' }}>🪑 Table sélectionnée : {getTableInfo(selectedTableId)?.name}</span>
                    <span style={{ color: colors.textMuted, fontSize: 12, marginLeft: 8 }}>({getTableInfo(selectedTableId)?.capacity} pers.)</span>
                  </div>
                  <button type="button" onClick={() => { setSelectedTableId(null); setForm({ ...form, table_id: '' }); }} style={{ background: 'transparent', border: 'none', color: colors.error, cursor: 'pointer', fontSize: 16 }}>✕</button>
                </div>
              )}

              {!selectedTableId && (
                <>
                  <label style={labelStyle}>Table (facultatif)</label>
                  <select value={form.table_id} onChange={e => { setForm({ ...form, table_id: e.target.value }); setSelectedTableId(e.target.value ? Number(e.target.value) : null); }} style={inputStyle}>
                    <option value="">Choisir sur le plan →</option>
                    {tables.map((t: any) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.capacity} pers.)</option>
                    ))}
                  </select>
                </>
              )}

              <label style={labelStyle}>Demandes spéciales</label>
              <textarea value={form.special_requests} onChange={e => setForm({ ...form, special_requests: e.target.value })} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />

              <button type="submit" disabled={busy} style={{ ...btnPrimary, marginTop: 8 }}>{busy ? 'Réservation...' : 'Réserver'}</button>
            </form>
          </div>
        </div>
      )}

      {/* MY RESERVATIONS TAB */}
      {tab === 'my' && (
        <div>
          {myRes.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: 40 }}>Aucune réservation</p>
          ) : (
            myRes.map((r: any) => (
              <div key={r.id} style={{ background: colors.card, borderRadius: radius.md, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: colors.text, fontSize: 16, fontWeight: 600 }}>{r.date} — {r.time_slot}</div>
                    <div style={{ color: colors.textMuted, fontSize: 13 }}>{r.guests_count} convives {r.special_requests ? `• ${r.special_requests}` : ''}</div>
                  </div>
                  <span style={{ background: `${statusColor[r.status] || colors.textMuted}20`, color: statusColor[r.status] || colors.textMuted, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>
                    {statusLabel[r.status] || r.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* CHECK-IN/OUT TAB */}
      {tab === 'checkin' && (
        <div style={{ maxWidth: 500 }}>
          <div style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
            <h3 style={{ color: colors.text, fontSize: 18, marginBottom: 12 }}>Check-in</h3>
            <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 16 }}>Arrivé au Siam ? Enregistrez votre présence pour accéder au module social.</p>
            {myRes.filter((r: any) => r.status === 'confirmed').map((r: any) => (
              <button key={r.id} onClick={() => handleCheckIn(r.id)} disabled={busy} style={{ ...btnPrimary, marginBottom: 8 }}>
                Check-in — {r.date} {r.time_slot}
              </button>
            ))}
            {myRes.filter((r: any) => r.status === 'confirmed').length === 0 && (
              <button onClick={() => handleCheckIn()} disabled={busy} style={btnPrimary}>Check-in sans réservation</button>
            )}
          </div>
          <div style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}` }}>
            <h3 style={{ color: colors.text, fontSize: 18, marginBottom: 12 }}>Check-out</h3>
            <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 16 }}>Quitter l établissement ? Votre mode social sera désactivé.</p>
            <button onClick={handleCheckOut} disabled={busy} style={{ ...btnSecondary, color: colors.error, borderColor: colors.error }}>Check-out</button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = { color: colors.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'block' };

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', background: colors.inputBg,
  border: `1px solid ${colors.border}`, borderRadius: radius.sm,
  color: colors.text, fontSize: 14, marginBottom: 16, outline: 'none',
  fontFamily: fonts.body, boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '12px', background: colors.gold, color: colors.bg,
  border: 'none', borderRadius: radius.sm, fontSize: 15, fontWeight: 'bold',
  cursor: 'pointer', fontFamily: fonts.body,
};

const btnSecondary: React.CSSProperties = {
  padding: '10px 20px', background: 'transparent', color: colors.gold,
  border: `1px solid ${colors.gold}`, borderRadius: radius.sm, fontSize: 14,
  cursor: 'pointer', fontFamily: fonts.body,
};

const tabStyle: React.CSSProperties = {
  padding: '8px 18px', background: colors.card, color: colors.textMuted,
  border: `1px solid ${colors.border}`, borderRadius: 20, cursor: 'pointer', fontSize: 13, fontFamily: fonts.body,
};

const tabActive: React.CSSProperties = {
  ...tabStyle, background: colors.gold, color: colors.bg, border: `1px solid ${colors.gold}`, fontWeight: 'bold',
};